import React, { useState } from "react";
import { BADGES } from "./cityData";
import { Zap, Plane, User, Music2, Compass, RotateCcw, LogOut, Volume2, VolumeX, ChevronDown, X, Trophy, ListChecks } from "lucide-react";

export default function CityMenu({ open, onClose, onFastTravel, soundOn, onToggleSound, onExit, onRestart, onTour, onStudio, onName, onPassport, quests = [], badges = [], musicVol = 0.15, onMusicVol }) {
  const [sec, setSec] = useState(null); // "badges" | "quests" | null
  if (!open) return null;
  const qd = quests.filter((q) => q.done).length;
  const Action = ({ icon: I, label, onClick, tone = "" }) => (
    <button onClick={onClick} className={`cq-px cq-menu-btn flex items-center gap-2 justify-center ${tone}`}><I size={13} />{label}</button>
  );
  return (
    <div className="absolute inset-0 z-40 bg-slate-950/85 backdrop-blur-sm flex items-center justify-center p-4" data-testid="city-menu" onClick={onClose}>
      <div className="w-full max-w-sm border-2 border-cyan-400/60 bg-[#070a1c] shadow-[0_0_40px_rgba(34,211,238,0.25)] max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-cyan-400/20 sticky top-0 bg-[#070a1c] z-10">
          <p className="cq-px text-cyan-300 text-sm cq-blink">PAUSED</p>
          <button onClick={onClose} className="cq-px text-slate-400 hover:text-white" data-testid="city-menu-close" aria-label="Resume"><X size={16} /></button>
        </div>
        <div className="p-4 space-y-3">
          <div className="grid grid-cols-2 gap-1.5">
            <Action icon={Zap} label="FAST TRAVEL" onClick={onFastTravel} tone="text-amber-300 border-amber-400/50" />
            <Action icon={Plane} label="PASSPORT" onClick={onPassport} />
            <Action icon={User} label="NAME / HAT" onClick={onName} />
            {onStudio && <Action icon={Music2} label="STUDIO PRO" onClick={onStudio} tone="text-cyan-300" />}
            {onTour && <Action icon={Compass} label="TAKE TOUR" onClick={onTour} tone="text-emerald-300" />}
            <Action icon={RotateCcw} label="RESTART" onClick={onRestart} />
          </div>

          <div className="space-y-1.5">
            <button onClick={() => setSec(sec === "badges" ? null : "badges")} className="w-full flex items-center justify-between cq-px text-[8px] text-amber-300 border border-amber-400/30 bg-amber-400/5 px-3 py-2" data-testid="city-badges-toggle">
              <span className="flex items-center gap-2"><Trophy size={12} /> BADGES · {badges.length}/{BADGES.length}</span>
              <ChevronDown size={12} className={`transition-transform ${sec === "badges" ? "rotate-180" : ""}`} />
            </button>
            {sec === "badges" && (
              <div className="grid grid-cols-2 gap-2 p-1" data-testid="city-badges">
                {BADGES.map((b) => { const has = badges.includes(b.id); return (
                  <div key={b.id} data-testid={`city-badge-${b.id}`} data-earned={has ? "1" : "0"} className={`border p-2 ${has ? "border-amber-400/60 bg-amber-400/10" : "border-slate-700 opacity-60"}`}>
                    <p className={`cq-px text-[8px] ${has ? "text-amber-200" : "text-slate-400"}`}>{has ? "★" : "☆"} {b.name}</p>
                    <p className="cq-px text-[6px] text-slate-400 mt-1 leading-relaxed">{has ? b.desc : b.how}</p>
                  </div>); })}
              </div>
            )}
            {quests.length > 0 && (<>
              <button onClick={() => setSec(sec === "quests" ? null : "quests")} className="w-full flex items-center justify-between cq-px text-[8px] text-emerald-300 border border-emerald-400/30 bg-emerald-400/5 px-3 py-2" data-testid="city-quests-toggle">
                <span className="flex items-center gap-2"><ListChecks size={12} /> QUESTS · {qd}/{quests.length}</span>
                <ChevronDown size={12} className={`transition-transform ${sec === "quests" ? "rotate-180" : ""}`} />
              </button>
              {sec === "quests" && (
                <div className="p-1" data-testid="city-quests">
                  {quests.map((q) => (
                    <div key={q.id} className="flex items-center justify-between gap-2 py-1" data-testid={`city-quest-${q.id}`}>
                      <span className={`cq-px text-[8px] ${q.done ? "text-emerald-300 line-through" : "text-slate-200"}`}>{q.done ? "✓" : "○"} {q.label}</span>
                      <span className="cq-px text-[8px] text-amber-300">{q.n}/{q.goal}</span>
                    </div>
                  ))}
                </div>
              )}
            </>)}
          </div>

          <div className="grid grid-cols-2 gap-1.5 border border-slate-700/60 p-2">
            <button onClick={onToggleSound} className="cq-px cq-menu-btn flex items-center gap-2 justify-center" data-testid="city-menu-sound">{soundOn ? <Volume2 size={13} /> : <VolumeX size={13} />} SND {soundOn ? "ON" : "OFF"}</button>
            {onMusicVol && <label className="cq-px cq-menu-btn flex items-center gap-2 cursor-pointer" data-testid="city-menu-music"><Music2 size={13} /><input type="range" min={0} max={60} value={Math.round(musicVol * 100)} onChange={(e) => onMusicVol(+e.target.value / 100)} className="flex-1 accent-fuchsia-400 min-w-0" data-testid="city-menu-music-vol" /></label>}
          </div>

          <div className="grid grid-cols-2 gap-1.5 pt-1">
            <button onClick={onClose} className="cq-px cq-menu-btn text-cyan-200" data-testid="city-menu-resume">▶ RESUME</button>
            <button onClick={onExit} className="cq-px cq-menu-btn text-amber-300 flex items-center gap-2 justify-center" data-testid="city-menu-exit"><LogOut size={13} /> EXIT</button>
          </div>
        </div>
      </div>
    </div>
  );
}
